import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFamily, getFamilyUsers, updateFamily } from '../services/adminService';
import { DisabledReason } from '../services/disabledReasonsService';
import { ArrowLeft, Plus } from 'lucide-react';
import BillingSection from '../components/BillingSection';
import DeactivateFamilyModal from '../components/DeactivateFamilyModal';
import './FamilyDetailScreen.css';

export default function FamilyDetailScreen() {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const { data: family, isLoading: familyLoading } = useQuery({
    queryKey: ['family', familyId],
    queryFn: () => getFamily(familyId!),
    enabled: !!familyId,
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['familyUsers', familyId],
    queryFn: () => getFamilyUsers(familyId!),
    enabled: !!familyId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) => updateFamily(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
      queryClient.invalidateQueries({ queryKey: ['familyUsers', familyId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDeactivateModal(false);
    },
  });

  const handleToggleActive = () => {
    if (!family) return;

    // If activating, just update directly
    if (family.isActive) {
      // Show deactivation modal
      setShowDeactivateModal(true);
    } else {
      // Reactivating - clear disabled reason fields
      updateMutation.mutate({
        id: family.id,
        updates: { isActive: true },
      });
    }
  };

  const handleDeactivate = (reason: DisabledReason) => {
    if (!family) return;

    updateMutation.mutate({
      id: family.id,
      updates: {
        isActive: false,
        disabledReason: reason.code,
        disabledReasonTitle: reason.title,
        disabledReasonMessage: reason.message,
      },
    });
  };

  if (familyLoading || usersLoading) {
    return <div className="loading-state">Loading family details...</div>;
  }

  if (!family) {
    return (
      <div className="error-state">
        <p>Family not found</p>
        <button className="primary-button" onClick={() => navigate('/families')}>
          Back to Families
        </button>
      </div>
    );
  }

  return (
    <div className="family-detail">
      <button className="back-button" onClick={() => navigate('/families')}>
        <ArrowLeft size={20} />
        <span>Back to Families</span>
      </button>

      <div className="detail-header">
        <div>
          <h1>{family.name}</h1>
          <p className="family-id">ID: {family.id}</p>
        </div>
        <div className="header-actions">
          <button
            className={`status-toggle ${family.isActive ? 'active' : 'inactive'}`}
            onClick={handleToggleActive}
            disabled={updateMutation.isPending}
          >
            {family.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-card">
          <h2>Subscription Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className="info-value">
                {typeof family.subscriptionStatus === 'string' 
                  ? family.subscriptionStatus 
                  : 'Not set'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Tier</span>
              <span className="info-value">
                {typeof family.subscriptionTier === 'string' 
                  ? family.subscriptionTier 
                  : 'Not set'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Stripe Customer</span>
              <span className="info-value">
                {(() => {
                  const customerId = family.stripeCustomerId;
                  if (!customerId) return 'Not set';
                  if (typeof customerId === 'string') return customerId;
                  if (typeof customerId === 'object' && customerId.id) return customerId.id;
                  return 'Invalid format';
                })()}
              </span>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <h2>User Limits</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Max Users</span>
              <span className="info-value">
                {typeof family.maxUsers === 'number' ? family.maxUsers : 'Unlimited'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Max Caregivers</span>
              <span className="info-value">
                {typeof family.maxCaregivers === 'number' ? family.maxCaregivers : 'Unlimited'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Max Members</span>
              <span className="info-value">
                {typeof family.maxFamilyMembers === 'number' ? family.maxFamilyMembers : 'Unlimited'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="users-section">
        <div className="section-header">
          <h2>Users ({users?.length || 0})</h2>
          <button className="primary-button">
            <Plus size={20} />
            <span>Add User</span>
          </button>
        </div>

        {users && users.length > 0 ? (
          <div className="users-list">
            {users.map((user) => (
              <div key={user.id} className="user-card">
                <div className="user-avatar">
                  {user.displayName[0].toUpperCase()}
                </div>
                <div className="user-info">
                  <div className="user-header">
                    <h3>{user.displayName}</h3>
                    {user.isActive === false && (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </div>
                  <p>{user.email}</p>
                  <div className="user-badges">
                    <span className={`role-badge ${user.role}`}>{user.role}</span>
                    {user.isActive !== false && (
                      <span className="status-badge active">Active</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">No users in this family yet.</div>
        )}
      </div>

      {family.disabledReason && !family.isActive && (
        <div className="detail-card disabled-reason-card">
          <h2>Deactivation Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Reason</span>
              <span className="info-value">{family.disabledReasonTitle || family.disabledReason}</span>
            </div>
            {family.disabledReasonMessage && (
              <div className="info-item full-width">
                <span className="info-label">Message</span>
                <span className="info-value">{family.disabledReasonMessage}</span>
              </div>
            )}
            {family.disabledAt && (
              <div className="info-item">
                <span className="info-label">Disabled At</span>
                <span className="info-value">
                  {family.disabledAt?.toDate ? 
                    family.disabledAt.toDate().toLocaleString() : 
                    'Unknown'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <BillingSection 
        familyId={family.id} 
        stripeCustomerId={typeof family.stripeCustomerId === 'string' ? family.stripeCustomerId : undefined}
      />

      <DeactivateFamilyModal
        familyName={family.name}
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleDeactivate}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
}

